import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { TranslateModule } from '@ngx-translate/core';
import { AuthService, Company, CompanyService, Enterprise, EnterpriseService, User } from '@core';
import { EnterpriseSimpleConfig } from '@core/models/enterprise-simple-config.model';
import { Partner } from '@core/models/partner.model';
import { MatInputModule } from '@angular/material/input';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { EnterpriseSelectionService } from '@core/service/enterprise-selection.service';
import { PeriodSelectionService } from '@core/service/period-selection.service';
import { PartnerService } from '@core/service/partner.service';
import { PartnerSelectionService } from '@core/service/partner-selection.service';
import { KeycloakService } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'enterprise-period',
  templateUrl: './enterprise-period.component.html',
  styleUrls: ['./enterprise-period.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    TranslateModule
]
})
export class EnterprisePeriodComponent implements OnInit, OnDestroy {
  selectedPartner: string | null = null;
  selectedCompany: string | null = null;
  selectedEnterprise: string | null = null;
  selectedPeriod: string | null = null;
  partners: Partner[] = [];
  companies: Company[] = []; // Change the type of 'companies' array to 'Company[]'
  enterprises: EnterpriseSimpleConfig[] = [];
  periods: string[] = [];
  isSuperAdmin = false;
  private periodSubscription?: Subscription;
  private userSubscription?: Subscription;
  private initialized = false;

  constructor(
    private authService: AuthService,
    private companyService: CompanyService,
    private enterpriseService: EnterpriseService,
    private companySelectionService: CompanySelectionService,
    private enterpriseSelectionService: EnterpriseSelectionService,
    private periodSelectionService: PeriodSelectionService,
    private partnerService: PartnerService,
    private keycloakService: KeycloakService,
    private cdr: ChangeDetectorRef,
    private partnerSelectionService: PartnerSelectionService
  ) {
  }

  ngOnInit() {
    // Subscribe to user changes — BehaviorSubject may fire synchronously if user already loaded
    this.userSubscription = this.authService.currentUser.pipe(
      filter(user => !!user && !!user.id)
    ).subscribe(user => {
      if (!this.initialized) {
        this.initialized = true;
        this.isSuperAdmin = this.checkIsSuperAdmin();
        console.log('[EnterprisePeriod] User ready (subscription), isSuperAdmin:', this.isSuperAdmin);
        this.bootstrapForUser(user);
      }
    });

    // Fallback: immediate check in case BehaviorSubject already has value but subscription didn't fire yet
    const currentUser = this.authService.currentUserValue;
    if (currentUser && currentUser.id && !this.initialized) {
      this.initialized = true;
      this.isSuperAdmin = this.checkIsSuperAdmin();
      console.log('[EnterprisePeriod] User ready (immediate), isSuperAdmin:', this.isSuperAdmin);
      this.bootstrapForUser(currentUser);
    }

    // Subscribe to period changes from other components (advance/revert period)
    this.periodSubscription = this.periodSelectionService.selectedPeriod$.subscribe((newPeriod) => {
      if (newPeriod && newPeriod !== this.selectedPeriod) {
        this.selectedPeriod = newPeriod;
        if (this.selectedCompany) {
          const key = 'enterprises-' + this.selectedCompany;
          const enterprisesJson = localStorage.getItem(key);
          if (enterprisesJson) {
            this.enterprises = JSON.parse(enterprisesJson);
          }
        }
      }
    });
  }

  /**
   * Same multi-fallback approach as role-redirect.component.ts
   * authService.isSuperAdmin() alone is not reliable in all timing scenarios.
   */
  private checkIsSuperAdmin(): boolean {
    const userRoles = this.keycloakService.getUserRoles();
    const realmRoles = this.keycloakService.getUserRoles(true);
    const keycloakInstance = this.keycloakService.getKeycloakInstance();
    return this.authService.isSuperAdmin() ||
           userRoles.includes('superAdmin') ||
           realmRoles.includes('superAdmin') ||
           (keycloakInstance?.tokenParsed?.realm_access?.roles?.includes('superAdmin') ?? false);
  }

  /**
   * Entry point after user is confirmed loaded. Branches by role.
   */
  private bootstrapForUser(user: User): void {
    if (this.isSuperAdmin) {
      // Reset any stale state from previous sessions — superAdmin must select partner first
      this.companies = [];
      this.enterprises = [];
      this.selectedCompany = null;
      this.selectedEnterprise = null;
      this.selectedPeriod = null;

      this.partnerService.getAllActive().subscribe({
        next: (partners) => {
          this.partners = partners;
          console.log('[EnterprisePeriod] Partners loaded:', partners.length);
          this.cdr.markForCheck();
          const storedPartner = localStorage.getItem('partner');
          if (storedPartner && this.partners.find(p => p.id === storedPartner)) {
            this.selectedPartner = storedPartner;
            this.partnerSelectionService.selectPartner(storedPartner);
            this.loadCompanies(); // restore previous partner selection
          }
        },
        error: (err) => console.error('[EnterprisePeriod] Error loading partners:', err)
      });
    } else {
      // Non-superAdmin: set partner silently from JWT claim
      if (user.partnerId) {
        this.selectedPartner = user.partnerId;
        localStorage.setItem('partner', user.partnerId);
        this.partnerSelectionService.selectPartner(user.partnerId);
      }
      this.initializeSelections(user);
      this.loadCompanies();
    }
  }

  /**
   * Initialize company and enterprise selections based on user data
   */
  private initializeSelections(user: User) {
    // superAdmin must go through partner → company → enterprise flow; skip auto-init
    if (this.isSuperAdmin) return;

    const userCompanyId = user?.companyId;
    const storedCompanyId = localStorage.getItem('company');
    
    console.log('[EnterprisePeriod] initializeSelections:', {
      userCompanyId,
      storedCompanyId,
      userEnterpriseIds: user?.enterpriseIds
    });
    
    // Use user's companyId from token if available, otherwise use localStorage
    if (userCompanyId) {
      console.log('[EnterprisePeriod] Using companyId from user token:', userCompanyId);
      this.selectedCompany = userCompanyId;
      localStorage.setItem('company', userCompanyId);
      this.companySelectionService.selectCompany(userCompanyId);
      // loadEnterprises will call autoSelectEnterprise after loading
      this.loadEnterprises();
    } else if (storedCompanyId) {
      console.log('[EnterprisePeriod] Using companyId from localStorage:', storedCompanyId);
      this.selectedCompany = storedCompanyId;
      this.companySelectionService.selectCompany(storedCompanyId);
      // loadEnterprises will call autoSelectEnterprise after loading
      this.loadEnterprises();
    }
  }

  ngOnDestroy() {
    if (this.periodSubscription) {
      this.periodSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  onPartnerSelected(event: any) {
    this.selectedPartner = event.value || null;
    if (this.selectedPartner) {
      localStorage.setItem('partner', this.selectedPartner);
    } else {
      localStorage.removeItem('partner');
    }
    this.partnerSelectionService.selectPartner(this.selectedPartner);
    // Reset company/enterprise chain
    this.selectedCompany = null;
    this.selectedEnterprise = null;
    this.selectedPeriod = null;
    this.companies = [];
    this.enterprises = [];
    localStorage.removeItem('companies');
    localStorage.removeItem('company');
    localStorage.removeItem('enterprise');
    localStorage.removeItem('period');
    this.companySelectionService.selectCompany('');
    this.enterpriseSelectionService.selectEnterprise('');
    this.loadCompanies();
  }

  onCompanySelected(event: any) {
    this.selectedCompany = event.value;
    localStorage.setItem('company', this.selectedCompany ?? '');
    this.resetEnterprises();
    this.companySelectionService.selectCompany(this.selectedCompany || '');
  }

  onEnterpriseSelected(event: any) {
    this.selectedEnterprise = event.value;
    localStorage.setItem('enterprise', this.selectedEnterprise ?? '');
    this.resetPeriod();
    this.enterpriseSelectionService.selectEnterprise(this.selectedEnterprise || '');
  }

  resetEnterprises() {
    this.selectedEnterprise = null;
    this.selectedPeriod = null;
    localStorage.removeItem('enterprise');
    this.loadEnterprises();
  }

  resetPeriod() {
    this.selectedPeriod = null;
    localStorage.removeItem('period');
    this.setPeriod();
  }

  loadCompanies() {
    var user = this.authService.currentUserValue;
    console.log('[EnterprisePeriod] Loading companies for user:', user?.id, 'companyId:', user?.companyId, 'partnerFilter:', this.selectedPartner);
    
    if (user && user.id) {
      // For superAdmin with no partner selected, show empty list until a partner is chosen
      if (this.isSuperAdmin && !this.selectedPartner) {
        this.companies = [];
        return;
      }

      // Check cache first (only for non-superAdmin or superAdmin with same partner)
      const cachedCompanies = localStorage.getItem('companies');
      if (cachedCompanies && !this.isSuperAdmin) {
        this.companies = JSON.parse(cachedCompanies);
        if (this.companies.length > 0) {
          console.log('[EnterprisePeriod] Companies loaded from cache:', this.companies.length);
          
          // If user has companyId from token and it's in the list, ensure it's selected
          if (user.companyId && !this.selectedCompany) {
            const userCompany = this.companies.find(c => c.id === user.companyId);
            if (userCompany) {
              this.selectedCompany = user.companyId;
              localStorage.setItem('company', user.companyId);
              this.companySelectionService.selectCompany(user.companyId);
              this.loadEnterprises();
            }
          }
          return;
        }
      }

      this.companyService.getCompaniesByUserId(user.id, this.selectedPartner ?? undefined).subscribe({
        next: (companies) => {
          console.log('[EnterprisePeriod] Companies loaded from API:', companies.length);
          this.companies = companies.map((company) => new Company(company.id, company.name, company.active));
          this.cdr.detectChanges();

          // If no companies returned but user has companyId in token, fetch company directly
          if (this.companies.length === 0 && user.companyId) {
            console.log('[EnterprisePeriod] No companies from user query, fetching by companyId:', user.companyId);
            this.companyService.getById(user.companyId).subscribe({
              next: (company) => {
                console.log('[EnterprisePeriod] Company loaded by ID:', company.name);
                this.companies = [new Company(company.id, company.name, company.active)];
                this.cacheAndSelectCompany(user);
              },
              error: (err) => {
                console.error('[EnterprisePeriod] Error loading company by ID:', err);
              }
            });
            return;
          }

          this.cacheAndSelectCompany(user);
        },
        error: (err) => {
          console.error('[EnterprisePeriod] Error loading companies:', err);
          
          // If error but user has companyId in token, try to fetch company directly
          if (user.companyId) {
            console.log('[EnterprisePeriod] Error on user query, trying to fetch by companyId:', user.companyId);
            this.companyService.getById(user.companyId).subscribe({
              next: (company) => {
                console.log('[EnterprisePeriod] Company loaded by ID (fallback):', company.name);
                this.companies = [new Company(company.id, company.name, company.active)];
                this.cacheAndSelectCompany(user);
              },
              error: (fallbackErr) => {
                console.error('[EnterprisePeriod] Error loading company by ID (fallback):', fallbackErr);
              }
            });
          }
        }
      });
    } else {
      console.warn('[EnterprisePeriod] No user available to load companies');
    }
  }

  private cacheAndSelectCompany(user: any) {
    const companiesString = JSON.stringify(this.companies);
    try {
      localStorage.setItem('companies', companiesString);
    } catch (e) {
      console.error('Error saving to localStorage', e);
    }
    
    // If user has companyId from token and it's in the list, ensure it's selected
    if (user.companyId && !this.selectedCompany) {
      const userCompany = this.companies.find((c: Company) => c.id === user.companyId);
      if (userCompany) {
        console.log('[EnterprisePeriod] Auto-selecting user company from token:', user.companyId);
        this.selectedCompany = user.companyId;
        localStorage.setItem('company', user.companyId);
        this.companySelectionService.selectCompany(user.companyId);
        this.loadEnterprises();
      }
    } else if (this.isSuperAdmin && !this.selectedCompany) {
      // superAdmin has no companyId in JWT — restore from localStorage
      const storedCompanyId = localStorage.getItem('company');
      if (storedCompanyId) {
        const storedCompany = this.companies.find((c: Company) => c.id === storedCompanyId);
        if (storedCompany) {
          console.log('[EnterprisePeriod] Restoring stored company for superAdmin:', storedCompanyId);
          this.selectedCompany = storedCompanyId;
          this.companySelectionService.selectCompany(storedCompanyId);
          this.cdr.detectChanges();
          this.loadEnterprises();
        }
      }
    }
  }

  loadEnterprises() {
    console.log('[EnterprisePeriod] Loading enterprises for company:', this.selectedCompany);
    
    if(this.selectedCompany) {
      const key = 'enterprises-' + this.selectedCompany;

      if (localStorage.getItem(key)) {
        this.enterprises = JSON.parse(localStorage.getItem(key) ?? '[]');
        if(this.enterprises.length > 0) {
          console.log('[EnterprisePeriod] Enterprises loaded from cache:', this.enterprises.length);
          // Auto-select enterprise after loading from cache
          this.autoSelectEnterprise();
          return;
        }
      }

      this.enterpriseService.getEnterpriseConfigsByCompanyId(this.selectedCompany).subscribe({
        next: (enterprises) => {
          console.log('[EnterprisePeriod] Enterprises loaded from API:', enterprises.length);
          this.enterprises = enterprises.map((enterprise: { id: string; cod: string; name: string; currentPeriod: string; initialPeriod: string; }) => new EnterpriseSimpleConfig(enterprise.id, enterprise.cod, enterprise.name, enterprise.currentPeriod, enterprise.initialPeriod));

          const enterprisesString = JSON.stringify(this.enterprises);
          try {
            localStorage.setItem(key, enterprisesString);
          }catch (e) {
            console.error('Error saving to localStorage', e);
          }
          
          // Auto-select enterprise after loading from API
          this.autoSelectEnterprise();
        },
        error: (err) => {
          console.error('[EnterprisePeriod] Error loading enterprises:', err);
        }
      });
    }
  }

  /**
   * Auto-select enterprise based on user's enterpriseIds from token
   */
  private autoSelectEnterprise() {
    // Skip if already selected
    if (this.selectedEnterprise) {
      console.log('[EnterprisePeriod] Enterprise already selected:', this.selectedEnterprise);
      return;
    }

    const user = this.authService.currentUserValue;
    // Normalize IDs to lowercase for comparison
    const userEnterpriseIds = (user?.enterpriseIds || []).map(id => id.toLowerCase());
    const storedEnterprise = localStorage.getItem('enterprise')?.toLowerCase() || null;

    console.log('[EnterprisePeriod] autoSelectEnterprise:', {
      userEnterpriseIds,
      storedEnterprise,
      availableEnterprises: this.enterprises.map(e => ({ id: e.id, name: e.name }))
    });

    // Check if stored enterprise is valid and in user's allowed list
    if (storedEnterprise) {
      const matchedEnterprise = this.enterprises.find(e => e.id.toLowerCase() === storedEnterprise);
      const isAllowed = userEnterpriseIds.length === 0 || userEnterpriseIds.includes(storedEnterprise);
      
      if (matchedEnterprise && isAllowed) {
        console.log('[EnterprisePeriod] Using stored enterprise:', matchedEnterprise.id);
        this.selectedEnterprise = matchedEnterprise.id;
        this.enterpriseSelectionService.selectEnterprise(matchedEnterprise.id);
        this.setPeriod();
        return;
      }
    }

    // If user has specific enterpriseIds from token
    if (userEnterpriseIds.length > 0) {
      // Filter to only enterprises that exist in the loaded list (case-insensitive)
      const validEnterprises = this.enterprises.filter(e => 
        userEnterpriseIds.includes(e.id.toLowerCase())
      );

      console.log('[EnterprisePeriod] Valid enterprises for user:', validEnterprises.map(e => ({ id: e.id, name: e.name })));

      if (validEnterprises.length === 1) {
        // Auto-select single enterprise
        console.log('[EnterprisePeriod] Auto-selecting single user enterprise:', validEnterprises[0].id);
        this.selectedEnterprise = validEnterprises[0].id;
        localStorage.setItem('enterprise', validEnterprises[0].id);
        this.enterpriseSelectionService.selectEnterprise(validEnterprises[0].id);
        this.setPeriod();
      } else if (validEnterprises.length > 1) {
        console.log('[EnterprisePeriod] User has multiple valid enterprises, waiting for selection');
      } else {
        console.warn('[EnterprisePeriod] No matching enterprises found for user IDs:', userEnterpriseIds);
      }
    } else if (this.enterprises.length === 1) {
      // User has no specific enterprises but only one exists - auto-select
      console.log('[EnterprisePeriod] Auto-selecting only available enterprise:', this.enterprises[0].id);
      this.selectedEnterprise = this.enterprises[0].id;
      localStorage.setItem('enterprise', this.enterprises[0].id);
      this.enterpriseSelectionService.selectEnterprise(this.enterprises[0].id);
      this.setPeriod();
    } else {
      console.log('[EnterprisePeriod] Multiple enterprises available, no auto-selection');
    }
  }

  setPeriod() {
    if(this.selectedCompany && this.selectedEnterprise) {
      const key = 'enterprises-' + this.selectedCompany;
      if (localStorage.getItem(key)) {
        this.enterprises = JSON.parse(localStorage.getItem(key) ?? '[]');

        let period = this.enterprises.find((enterprise) => enterprise.id === this.selectedEnterprise)?.CurrentPeriod ?? null;
        // Normalizar formato do período para yyyy-MM (substituir / por -)
        if (period && period.includes('/')) {
          period = period.replace('/', '-');
        }
        this.selectedPeriod = period;
        localStorage.setItem('period', this.selectedPeriod ?? '');
      }
    }
  }
}
